import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS, ROLE_BADGE } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { UserCircle2, KeyRound, Save, Mail, AtSign, Clock, Camera } from "lucide-react";

const fmtLogin = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return "-"; }
};

export default function Profile() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user.name);
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busyName, setBusyName] = useState(false);
  const [busyPass, setBusyPass] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    let url;
    if (user.photo) {
      api.get(`/files/${user.photo}`, { responseType: "blob" })
        .then((r) => { url = URL.createObjectURL(r.data); setPhotoUrl(url); })
        .catch(() => {});
    }
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [user.photo]);

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("File harus berupa gambar"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Ukuran gambar maksimal 5MB"); return; }
    setPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/auth/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Foto profil diperbarui");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPhotoBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const saveName = async () => {
    if (!name.trim()) { toast.error("Nama tampilan tidak boleh kosong"); return; }
    setBusyName(true);
    try {
      await api.post("/auth/profile", { name: name.trim() });
      await refresh();
      toast.success("Nama tampilan diperbarui — berlaku di seluruh laporan");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyName(false); }
  };

  const savePassword = async () => {
    if (!cur || !next) { toast.error("Isi password saat ini dan password baru"); return; }
    if (next.length < 6) { toast.error("Password baru minimal 6 karakter"); return; }
    if (next !== confirm) { toast.error("Konfirmasi password tidak sama"); return; }
    setBusyPass(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password berhasil diubah");
      setCur(""); setNext(""); setConfirm("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyPass(false); }
  };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="profile-page">
      <div>
        <h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight">Profil Saya</h2>
        <p className="text-xs sm:text-sm text-muted-foreground">Kelola nama tampilan dan password akun Anda.</p>
      </div>

      <Card className="p-4 sm:p-5">
        <div className="flex items-center gap-3">
          <div className="relative shrink-0">
            <div className="grid place-items-center h-16 w-16 rounded-full bg-primary/10 border border-[#C9A227]/40 overflow-hidden" data-testid="profile-avatar">
              {photoUrl
                ? <img src={photoUrl} alt="Foto profil" className="h-full w-full object-cover" />
                : <UserCircle2 className="h-8 w-8 text-primary" />}
            </div>
            <button type="button" onClick={() => fileRef.current?.click()} disabled={photoBusy}
              className="absolute -bottom-1 -right-1 grid place-items-center h-6 w-6 rounded-full bg-[#C9A227] text-white shadow hover:opacity-90 disabled:opacity-50"
              title="Ganti foto profil" data-testid="profile-photo-btn">
              <Camera className="h-3 w-3" />
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="profile-photo-input"
              onChange={(e) => uploadPhoto(e.target.files?.[0])} />
          </div>
          <div className="min-w-0">
            <p className="font-heading text-base font-bold truncate" data-testid="profile-name-display">{user.name}</p>
            <Badge variant="outline" className={`mt-0.5 text-[10px] px-1.5 py-0 ${ROLE_BADGE[user.role]}`}>{ROLE_LABELS[user.role]}</Badge>
            <p className="text-[10px] text-muted-foreground mt-1">{photoBusy ? "Mengunggah foto..." : "Ketuk ikon kamera untuk mengganti foto profil"}</p>
          </div>
        </div>
        <div className="mt-3 space-y-1.5 text-xs text-muted-foreground">
          <p className="flex items-center gap-1.5"><AtSign className="h-3.5 w-3.5 shrink-0" />{user.username}</p>
          {user.email && <p className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 shrink-0" />{user.email}</p>}
          <p className="flex items-center gap-1.5" data-testid="profile-last-login">
            <Clock className="h-3.5 w-3.5 shrink-0" />
            {user.prev_login_at ? `Login terakhir: ${fmtLogin(user.prev_login_at)}` : "Ini login pertama Anda"}
          </p>
        </div>
      </Card>

      <Card className="p-4 sm:p-5 space-y-3">
        <h3 className="font-heading text-sm font-semibold flex items-center gap-1.5"><UserCircle2 className="h-4 w-4 text-primary" /> Nama Tampilan</h3>
        <div className="space-y-1.5">
          <Label className="text-xs">Nama</Label>
          <Input data-testid="profile-name-input" className="h-9 text-sm" value={name} onChange={(e) => setName(e.target.value)} />
          <p className="text-[11px] text-muted-foreground">Nama ini dipakai sebagai "Oleh/Dibuat oleh" di transaksi, laporan, dan riwayat.</p>
        </div>
        <div className="flex justify-end">
          <Button onClick={saveName} disabled={busyName} data-testid="profile-name-save" variant="gold" size="sm" className="h-8 text-xs gap-1.5">
            <Save className="h-3.5 w-3.5" /> Simpan Nama
          </Button>
        </div>
      </Card>

      <Card className="p-4 sm:p-5 space-y-3">
        <h3 className="font-heading text-sm font-semibold flex items-center gap-1.5"><KeyRound className="h-4 w-4 text-primary" /> Ganti Password</h3>
        <div className="space-y-2.5">
          <div className="space-y-1">
            <Label className="text-xs">Password Saat Ini</Label>
            <Input data-testid="profile-current-password" type="password" className="h-9 text-sm" value={cur} onChange={(e) => setCur(e.target.value)} autoComplete="current-password" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <div className="space-y-1">
              <Label className="text-xs">Password Baru</Label>
              <Input data-testid="profile-new-password" type="password" className="h-9 text-sm" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Konfirmasi Password Baru</Label>
              <Input data-testid="profile-confirm-password" type="password" className="h-9 text-sm" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">Minimal 6 karakter. Login berikutnya memakai password baru.</p>
        </div>
        <div className="flex justify-end">
          <Button onClick={savePassword} disabled={busyPass} data-testid="profile-password-save" variant="gold" size="sm" className="h-8 text-xs gap-1.5">
            <KeyRound className="h-3.5 w-3.5" /> Simpan Password
          </Button>
        </div>
      </Card>
    </div>
  );
}
